import React from 'react'
import styled from 'styled-components'
import { camelCase, startCase } from 'lodash'
import { Link } from 'gatsby'

import { Box, H3, Card, Chip, P, Icon } from '@allied-solutions/affinity'
import { ArrowRight } from '@allied-solutions/affinity-react-icons/dist/standard'

const CardContainer = styled(Box)`
  display: flex;
  width: 100%;
  min-width: 0;
  transition: transform 0.2s ease-in-out;

  ${props => props.theme.mediaQueries.sm} {
    flex: 1 1 0%;
  }

  ${H3} {
    transition: color 0.2s ease-in-out;
  }

  &:has(a:hover),
  &:has(a:focus) {
    transform: translateY(-1rem);

    ${H3} {
      color: ${props => props.theme.colors.blue};
    }
  }
`

const StretchedLink = styled(Link)`
  position: absolute;
  inset: 0;
  z-index: 1;
  border-radius: inherit;
  text-decoration: none;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.blue};
    outline-offset: 2px;
  }
`

const StyledCard = styled(Card)`
  position: relative;
  flex: 1;
  width: 100%;
  max-width: none;
  min-width: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${props => props.theme.radii.xl};

  ${props => props.theme.mediaQueries.sm} {
    height: 100%;
  }

  ${Card.Footer} {
    border-top: none;
    flex-shrink: 0;
  }
`

const TitleBox = styled(Box)`
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;

  ${props => props.theme.mediaQueries.sm} {
    /* Matches titleMedium.bold line-height (28px) for up to 3 lines */
    min-height: calc(28px * 3);
  }
`

const StyledChip = styled(Chip)`
  padding: ${props => props.theme.space['xl']}
    ${props => props.theme.space['md']};
`

const getChipVariant = tag => {
  if (tag === 'guide') {
    return 'teal'
  }
  if (tag === 'learn') {
    return 'yellow'
  }
  return 'blue'
}

const ArticleCard = ({ title, tag, path, ...rest }) => {
  const baseId = `ArticleCard--${camelCase(title)}`
  const tagId = `${baseId}__tag`
  const titleId = `${baseId}__title`
  const readMoreId = `${baseId}__read-more`

  return (
    <CardContainer mb={['2xl', 0]}>
      <StyledCard>
        <Card.Header>
          <StyledChip variant={getChipVariant(tag)} rounded>
            <span id={tagId}>{startCase(tag)}</span>
          </StyledChip>
        </Card.Header>
        <TitleBox>
          <H3
            id={titleId}
            typeStyle="titleMedium.bold"
            color="text.default"
            mb={0}
            mt="xl"
          >
            {title}
          </H3>
        </TitleBox>
        <Card.Footer>
          <Box display="flex" alignItems="center" gap="md">
            <P
              id={readMoreId}
              typeStyle="labelLarge.medium"
              color="text.brand"
              mb={0}
              css={`
                display: inline-block;
                cursor: pointer;
              `}
            >
              Read more
            </P>
            <Icon src={ArrowRight} aria-hidden color="brand" />
          </Box>
        </Card.Footer>
        <StretchedLink
          id={`${baseId}__StyledLink`}
          to={path}
          aria-labelledby={`${tagId} ${titleId} ${readMoreId}`}
          {...rest}
        />
      </StyledCard>
    </CardContainer>
  )
}

export { ArticleCard }
